/* Theme-local adapter for the store's existing official Yotpo widget. */
(() => {
  if (window.astraReviewsInitialized) return;
  window.astraReviewsInitialized = true;
  const loaders = new Map();
  const messages = {
    en: {
      loading: 'Loading reviews…',
      unavailable: 'Written reviews are temporarily unavailable. Please try again.',
      empty: 'Written reviews are not available here yet.',
    },
    fr: {
      loading: 'Chargement des avis…',
      unavailable: 'Les avis écrits sont temporairement indisponibles. Veuillez réessayer.',
      empty: 'Les avis écrits ne sont pas encore disponibles ici.',
    },
  };

  // Products without standard rating metafields use their own provider result.
  // Never borrow another colour/product's score or show zero before that check.
  function updateMissingSummary(section, result) {
    const french = section.dataset.language === 'fr';
    document.querySelectorAll('[data-astra-rating-source="provider"]').forEach(summary => {
      if (summary.dataset.astraRatingProduct !== section.dataset.productId) return;
      if (!result && summary.dataset.astraRatingResolved === 'true') return;
      const label = summary.querySelector('[data-astra-rating-label]');
      summary.removeAttribute('aria-busy');
      summary.classList.remove('astra-rating-summary--pending');
      if (!result) {
        label.textContent = french ? 'Avis indisponibles' : 'Reviews unavailable';
        return;
      }
      summary.dataset.astraRatingResolved = 'true';
      const count = result.count;
      const score = result.score;
      if (count === 0) {
        summary.classList.add('astra-rating-summary--empty');
        label.textContent = french ? '0 avis' : '0 reviews';
        summary.setAttribute('aria-label', french ? 'Pas encore de note · 0 avis' : 'Not yet rated · 0 reviews');
        return;
      }
      const validScore = Number.isFinite(score) && score > 0 && score <= 5;
      label.textContent = `${validScore ? `${Math.round(score * 10) / 10}/5 · ` : ''}${count} ${french ? 'avis' : count === 1 ? 'review' : 'reviews'}`;
      if (validScore) {
        summary.querySelector('.astra-rating-stars > span:first-child').textContent = '★★★★★';
        summary.querySelector('.astra-rating-stars > span:last-child').style.width = `${score * 20}%`;
      }
      if (summary.dataset.astraRatingContext === 'section') return;
      const link = document.createElement('a');
      link.className = 'astra-rating-summary';
      link.href = '#AstraProductReviews';
      link.dataset.astraReviewLink = '';
      while (summary.firstChild) link.appendChild(summary.firstChild);
      summary.replaceWith(link);
    });
  }

  // Keep our shared total beside the native Write a Review button. Reattach only
  // if the provider replaces its header during filtering or pagination.
  function attachOverallSummary(section, mount) {
    const overall = section.querySelector('[data-review-overall]');
    const place = () => {
      const header = mount.querySelector('.yotpo-layout-header-wrapper');
      if (header && overall.parentElement !== header) header.prepend(overall);
    };
    const observer = new MutationObserver(place);
    observer.observe(mount, { childList: true, subtree: true });
    place();
    section.astraReviewObserver = observer;
  }

  const until = (check, timeout = 20000) => new Promise((resolve, reject) => {
    const started = Date.now();
    const poll = () => {
      if (check()) return resolve();
      if (Date.now() - started >= timeout) return reject(new Error('Review widget timed out'));
      window.setTimeout(poll, 150);
    };
    poll();
  });

  async function loadProvider(key, language, mount) {
    if (!loaders.has(key)) {
      const promise = new Promise((resolve, reject) => {
        const url = `https://cdn-widgetsrepository.yotpo.com/v1/loader/${encodeURIComponent(key)}`;
        const existing = Array.from(document.scripts).find((script) => script.src.startsWith(url));
        if (existing) return resolve();
        const script = document.createElement('script');
        script.src = `${url}?languageCode=${encodeURIComponent(language)}`;
        script.async = true;
        script.onload = resolve;
        script.onerror = () => { script.remove(); reject(new Error('Review provider unavailable')); };
        document.head.appendChild(script);
      }).then(() => until(() => typeof window.yotpoWidgetsContainer?.initWidget === 'function'));
      loaders.set(key, promise);
      promise.catch(() => loaders.delete(key));
    }
    await loaders.get(key);
    // The loader initializes existing placeholders itself. Initialize a new or
    // missed placeholder, and retry a mount whose earlier render failed.
    try {
      // Yotpo marks the placeholder loaded before its async render completes.
      // A timed-out mount needs another initialization even with that marker.
      if (mount.getAttribute('data-yotpo-element-loaded') !== 'true' || mount.dataset.astraReviewInitFailed === 'true') {
        delete mount.dataset.astraReviewInitFailed;
        await window.yotpoWidgetsContainer.initWidget(679392, mount);
      }
      await until(() => Boolean(mount.querySelector('.yotpo-main-widget-layout-reviews')));
    } catch (error) {
      mount.dataset.astraReviewInitFailed = 'true';
      throw error;
    }
  }

  async function getAvailability(section) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 12000);
    try {
      // Use the same V3 source as the native widget, including Shop reviews.
      // A first-page read checks availability; no catalogue or rating is changed.
      const key = encodeURIComponent(section.dataset.appKey);
      const product = encodeURIComponent(section.dataset.productId);
      const response = await fetch(`https://api-cdn.yotpo.com/v3/storefront/store/${key}/product/${product}/reviews?page=1&perPage=1`, { signal: controller.signal, credentials: 'omit' });
      if (!response.ok) throw new Error('Review availability unavailable');
      const data = await response.json();
      const count = Number(data.pagination?.total);
      const score = Number(data.bottomline?.averageScore);
      if (!Number.isFinite(count) || count < 0) throw new Error('Invalid review availability');
      return { count, score };
    } finally {
      window.clearTimeout(timer);
    }
  }

  function materializeWidget(widget) {
    // Hidden elements are still found by the app's global scanner. Keep the
    // placeholder in inert template content until the shopper requests reviews.
    // Reuse a mounted placeholder after failure (and older cached markup).
    const existing = widget.querySelector('.yotpo-widget-instance');
    if (existing) return existing;
    const template = widget.querySelector('[data-astra-review-template]');
    const placeholder = template?.content.querySelector('.yotpo-widget-instance');
    if (!placeholder) throw new Error('Review placeholder unavailable');
    const mount = placeholder.cloneNode(true);
    widget.appendChild(mount);
    return mount;
  }

  function prepare(section) {
    if (section.dataset.reviewPrepared) return;
    section.dataset.reviewPrepared = 'true';
    const copy = messages[section.dataset.language] || messages.en;
    const status = section.querySelector('[data-review-status]');
    section.querySelector('[data-review-source]')?.remove();
    const retry = section.querySelector('[data-review-retry]');
    const widget = section.querySelector('[data-review-widget]');
    const storedCount = Number(section.dataset.storedCount) || 0;
    const confirmedSummary = Boolean(section.querySelector('[data-astra-rating-source="merchant-confirmed"]'));
    let running = false;
    let loaded = false;
    let availabilityPromise;

    const showError = () => {
      updateMissingSummary(section, null);
      section.hidden = false;
      status.textContent = copy.unavailable;
      status.hidden = false;
      retry.hidden = false;
    };

    // Resolve missing ATC ratings immediately, without paying for the written
    // review UI until the shopper approaches it or follows the review link.
    // Share this read with the widget path and discard failures for Retry.
    const availability = () => {
      if (!availabilityPromise) {
        availabilityPromise = getAvailability(section).then(result => {
          if (!section.isConnected) return result;
          updateMissingSummary(section, result);
          if (result.count === 0) {
            section.hidden = storedCount === 0 && !confirmedSummary;
            status.textContent = copy.empty;
          } else {
            section.hidden = false;
          }
          return result;
        }).catch(error => {
          availabilityPromise = null;
          throw error;
        });
      }
      return availabilityPromise;
    };

    const start = async () => {
      if (running || loaded) return;
      running = true;
      status.textContent = copy.loading;
      status.hidden = false;
      retry.hidden = true;
      try {
        const { count } = await availability();
        if (!section.isConnected) return;
        if (count === 0) {
          section.hidden = storedCount === 0 && !confirmedSummary;
          status.textContent = copy.empty;
          loaded = true;
          return;
        }
        section.hidden = false;
        const mount = materializeWidget(widget);
        widget.hidden = false;
        await loadProvider(section.dataset.appKey, section.dataset.language, mount);
        attachOverallSummary(section, mount);
        status.hidden = true;
        loaded = true;
      } catch (_) {
        showError();
      } finally {
        running = false;
      }
    };

    retry.addEventListener('click', start);
    section.astraLoadReviews = start;
    if (window.location.hash === '#AstraProductReviews') {
      start();
    } else if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          start();
        }
      }, { rootMargin: '500px 0px' });
      observer.observe(section);
      section.astraReviewVisibilityObserver = observer;
      if (storedCount === 0 && !confirmedSummary) availability().catch(showError);
    } else {
      start();
    }
  }

  function init() {
    document.querySelectorAll('[data-astra-reviews]').forEach(prepare);
  }
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-astra-review-link], a[href="#AstraProductReviews"]')) {
      document.getElementById('AstraProductReviews')?.astraLoadReviews?.();
    }
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-astra-reviews]').forEach(section => {
      section.astraReviewObserver?.disconnect();
      section.astraReviewVisibilityObserver?.disconnect();
    });
  });
  init();
})();
